<script setup lang="ts">
import {computed} from 'vue'
import {Check, ChevronDown, CircleCheck, CircleHelp, Flag, LoaderCircle, MessageCircle} from 'lucide-vue-next'
import {useTranslationsStore} from '@/stores/translations'
import {Button} from '@/components/ui/button'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import type {Material, TranslatedMaterial} from '@shared/types'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'

const props = defineProps<{
  material: Material | null
  translation: TranslatedMaterial | null
  nativeLang: string
  translationStatuses: {translate_id: number; material_id: number; status: 'check' | 'ok' | 'sample'}[]
}>()

const emit = defineEmits<{
  translationUpdated: []
  goToNextCheck: []
  statusUpdated: [translateId: number, status: 'check' | 'ok' | 'sample']
}>()

const translationsStore = useTranslationsStore()
const isGeneratingTranslation = computed(() => translationsStore.isGeneratingTranslation)
const editedTranslation = computed(() => translationsStore.editedTranslation)
const isTranslationChanged = computed(() => {
  if (!props.translation || !editedTranslation.value.data.length) return false
  const titleChanged = (props.translation.title || '') !== (editedTranslation.value.title || '')
  const dataChanged = props.translation.data.some((item, index) => {
    const edited = editedTranslation.value.data[index]
    return (item.default || '') !== (edited?.default || '') || (item.commented || '') !== (edited?.commented || '')
  })
  return titleChanged || dataChanged
})

function getImageUrl(image: string): string {
  return translationsStore.getImageUrl(image)
}
function postprocessCommentedTranslation(commented: string): string {
  return translationsStore.postprocessCommentedTranslation(commented)
}
async function generateTranslation(): Promise<void> {
  await translationsStore.generateTranslation()
  emit('translationUpdated')
}
async function generateCommentedTranslation(): Promise<void> {
  await translationsStore.generateCommentedTranslation()
  emit('translationUpdated')
}
async function generateDefaultTranslation(): Promise<void> {
  await translationsStore.generateDefaultTranslation()
  emit('translationUpdated')
}
async function generateCommentedTranslationForSentence(index: number): Promise<void> {
  await translationsStore.generateCommentedTranslationForSentence(index)
  emit('translationUpdated')
}

async function saveTranslation(status: 'check' | 'ok' | 'sample', goNext = false): Promise<void> {
  const result = await translationsStore.saveTranslation(status)
  if (result && props.translation?.translate_id) emit('statusUpdated', props.translation.translate_id, status)
  emit('translationUpdated')
  if (goNext) emit('goToNextCheck')
}

async function updateStatus(status: 'ok' | 'sample', goNext = false): Promise<void> {
  if (!props.translation) return
  const result = await translationsStore.updateStatus(status)
  if (!result) return
  emit('statusUpdated', props.translation.translate_id, status)
  if (goNext) emit('goToNextCheck')
}
</script>

<template>
  <div v-if="material" class="space-y-6">
    <div v-if="translation" class="flex flex-wrap items-end gap-3 border-b pb-4">
      <component
        :is="translation.status === 'ok' ? CircleCheck : translation.status === 'sample' ? Flag : CircleHelp"
        class="mb-2 size-5 text-muted-foreground" />
      <div class="min-w-[220px] flex-1">
        <Label for="translation-title">Title</Label
        ><Input id="translation-title" v-model="editedTranslation.title" class="mt-2" />
      </div>
      <div class="flex">
        <Button class="rounded-r-none" :disabled="isGeneratingTranslation" @click="generateTranslation">
          <LoaderCircle v-if="isGeneratingTranslation" class="mr-2 size-4 animate-spin" />
          Generate Translation
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger as-child
            ><Button
              size="icon"
              class="rounded-l-none border-l border-primary-foreground/20"
              :disabled="isGeneratingTranslation"
              ><ChevronDown class="size-4" /></Button
          ></DropdownMenuTrigger>
          <DropdownMenuContent align="end"
            ><DropdownMenuItem @click="generateDefaultTranslation">Generate Default Only</DropdownMenuItem
            ><DropdownMenuItem @click="generateCommentedTranslation"
              >Generate Commented Only</DropdownMenuItem
            ></DropdownMenuContent
          >
        </DropdownMenu>
      </div>
    </div>
    <article
      v-for="(item, index) in material.data"
      :key="index"
      class="grid gap-5 border-b pb-6 last:border-b-0 lg:grid-cols-[minmax(220px,300px)_minmax(0,1fr)]">
      <img
        :src="getImageUrl(item.image)"
        :alt="item.description?.short || item.text"
        class="w-full rounded-md border object-contain" />
      <div class="space-y-4">
        <div class="grid gap-2">
          <Label :for="`source-${index}`">Source</Label
          ><Textarea :id="`source-${index}`" :model-value="item.text" readonly />
        </div>
        <div v-if="editedTranslation?.data[index]" class="grid gap-2">
          <Label :for="`translation-${index}`">Translation</Label
          ><Textarea :id="`translation-${index}`" v-model="editedTranslation.data[index].default" />
        </div>
        <div v-if="editedTranslation?.data[index]" class="grid gap-2">
          <div class="flex items-center justify-between">
            <Label :for="`commented-${index}`">Commented translation</Label>
            <TooltipProvider
              ><Tooltip
                ><TooltipTrigger as-child
                  ><Button
                    size="icon"
                    variant="outline"
                    :disabled="isGeneratingTranslation"
                    @click="generateCommentedTranslationForSentence(index)"
                    ><MessageCircle class="size-4" /></Button></TooltipTrigger
                ><TooltipContent>Generate commented translation for this sentence</TooltipContent></Tooltip
              ></TooltipProvider
            >
          </div>
          <div
            class="commented-translation"
            v-html="
              postprocessCommentedTranslation(
                editedTranslation.data[index].commented || editedTranslation.data[index].default
              )
            " />
          <Textarea :id="`commented-${index}`" v-model="editedTranslation.data[index].commented" />
        </div>
        <p v-if="editedTranslation?.data[index]?.error" class="text-sm text-destructive">
          {{ editedTranslation.data[index].error }}
        </p>
      </div>
    </article>
  </div>
  <div
    v-if="translation"
    class="sticky bottom-0 z-10 flex items-center justify-between gap-4 border-t bg-background/95 py-4 backdrop-blur">
    <div class="flex flex-wrap gap-2">
      <template v-if="isTranslationChanged"
        ><Button @click="saveTranslation('ok')"><Check class="mr-2 size-4" />Save as OK</Button
        ><Button variant="secondary" @click="saveTranslation('sample')"
          ><Flag class="mr-2 size-4" />Save as Sample</Button
        ></template
      >
      <template v-else
        ><Button v-if="translation.status !== 'sample'" variant="outline" @click="updateStatus('sample')"
          ><Flag class="mr-2 size-4" />Set Sample</Button
        ><Button v-if="translation.status !== 'ok'" variant="outline" @click="updateStatus('ok')"
          ><Check class="mr-2 size-4" />Set OK</Button
        ></template
      >
    </div>
    <Button v-if="isTranslationChanged" variant="outline" @click="saveTranslation('sample', true)"
      >Save and go next</Button
    >
    <Button v-else-if="translation.status !== 'ok'" @click="updateStatus('ok', true)"
      ><Check class="mr-2 size-4" />Set OK and go next</Button
    >
  </div>
</template>

<style scoped>
.commented-translation {
  padding-bottom: 1em;
  line-height: 2.2em;
  text-align: center;
}

.commented-translation :deep(span) {
  position: relative;
  text-decoration: underline dotted;
  text-underline-offset: 4px;
}

.commented-translation :deep(span::after) {
  position: absolute;
  top: 2em;
  left: 50%;
  opacity: 0.75;
  content: '(' attr(title) ')';
  font-size: 0.75em;
  line-height: 1.1em;
  text-align: center;
  white-space: nowrap;
  translate: -50% 0;
}
</style>
